import { authFetch } from "@/lib/api-client";
import type { BulkJobStatusResponse } from "@nexus/contracts";
import type { CreativeExample, Opportunity } from "../types/opportunities.types";

// ─── Scripts ─────────────────────────────────────────────────────────

export interface ScriptScene {
  id: string;
  scene_number: number;
  scene_name: string;
  props: string;
  seeing: string;
  hearing: string;
  product?: string;
}

export interface ScriptDto {
  id: string;
  opportunityId: number;
  opportunityTitle: string | null;
  opportunityDescription: string | null;
  opportunityFlowName: string | null;
  opportunityEngine: string | null;
  adDescription: string | null;
  product: string | null;
  background: string | null;
  concept: string | null;
  duration: string | null;
  hook: string | null;
  isFavorite: boolean;
  presenter: string | null;
  rationale: string | null;
  scenes: ScriptScene[];
  script: string | null;
  sequence: string | null;
  isAiEdited: boolean;
  canRevert: boolean;
  scenesCreatedAt: string | null;
  previousVersionCreatedAt: string | null;
  createdAt: string;
  storyboardStatus: "NONE" | "GENERATING" | "READY" | "FAILED";
  /** One rendered storyboard per image model (for comparison). */
  storyboardImages: Array<{ model: string; imageUrl: string | null }>;
  /** Resolved image prompt sent to the model(s) — shown in the debug modal. */
  storyboardImagePrompt: string | null;
  /** Photoreal per-panel variant lifecycle, independent of `storyboardStatus`. */
  storyboardPanelStatus: "NONE" | "GENERATING" | "READY" | "FAILED";
  /** Photoreal frames in playback order. `label`/`caption` are rendered as text, not baked into the image. */
  storyboardPanels: StoryboardPanel[];
}

/** One photoreal frame of the animatic, in playback order. */
export interface StoryboardPanel {
  panelIndex: number;
  /**
   * Scene this frame depicts. Several frames share a scene, so playback divides that
   * scene's spoken line between them. Absent on frames generated before the pairing.
   */
  sceneIndex?: number;
  label: string;
  caption: string;
  imageUrl: string | null;
}

/** Which storyboard variant to render: the sketch sheet, or photoreal frames. */
export type StoryboardVariant = "sketch" | "panels";

export async function aiEditScripts(setId: string, scriptIds: string[], instruction: string, tenantId: string): Promise<{ correlationId: string }> {
  const idempotencyKey = crypto.randomUUID();
  const { correlationId } = await authFetch<void>(`/opportunity-sets/${encodeURIComponent(setId)}/scripts/ai-edit`, {
    method: "POST",
    body: JSON.stringify({ tenantId, setId, scriptIds, instruction, idempotencyKey }),
  });
  return { correlationId: correlationId! };
}

export async function suggestScripts(
  setId: string,
  description: string,
  variations: number,
  tenantId: string,
  referenceUrls: string[] = [],
): Promise<{ correlationId: string }> {
  const idempotencyKey = crypto.randomUUID();
  const { correlationId } = await authFetch<void>(
    `/opportunity-sets/${encodeURIComponent(setId)}/scripts/suggest`,
    {
      method: "POST",
      body: JSON.stringify({ tenantId, setId, description, variations, referenceUrls, idempotencyKey }),
    },
  );
  return { correlationId: correlationId! };
}

/** Per-URL outcome of the resolve-links pre-step (engine fetch result). */
export interface ResolvedLinkResult {
  url: string;
  ok: boolean;
  reason: string;
}

export async function resolveReferenceLinks(
  setId: string,
  referenceUrls: string[],
  tenantId: string,
): Promise<{ correlationId: string }> {
  const idempotencyKey = crypto.randomUUID();
  const { correlationId } = await authFetch<void>(
    `/opportunity-sets/${encodeURIComponent(setId)}/scripts/resolve-links`,
    {
      method: "POST",
      body: JSON.stringify({ tenantId, setId, referenceUrls, idempotencyKey }),
    },
  );
  return { correlationId: correlationId! };
}

/**
 * Amber Path — enqueue storyboard image generation for a single script.
 * Returns a correlationId for status polling.
 */
export async function generateStoryboard(
  setId: string,
  scriptId: string,
  tenantId: string,
  variant: StoryboardVariant = "sketch",
): Promise<{ correlationId: string }> {
  const idempotencyKey = crypto.randomUUID();
  const { correlationId } = await authFetch<void>(
    `/opportunity-sets/${encodeURIComponent(setId)}/scripts/${encodeURIComponent(scriptId)}/storyboard`,
    {
      method: "POST",
      body: JSON.stringify({ tenantId, idempotencyKey, variant }),
    },
  );
  return { correlationId: correlationId! };
}

export async function revertScript(setId: string, scriptId: string): Promise<ScriptDto> {
  const { data } = await authFetch<ScriptDto>(
    `/opportunity-sets/${encodeURIComponent(setId)}/scripts/${encodeURIComponent(scriptId)}/revert`,
    { method: "PATCH" },
  );
  return data;
}

export async function reorderScriptScenes(setId: string, scriptId: string, sceneIds: string[]): Promise<void> {
  await authFetch(`/opportunity-sets/${encodeURIComponent(setId)}/scripts/${encodeURIComponent(scriptId)}/scenes/reorder`, {
    method: "PATCH",
    body: JSON.stringify({ sceneIds }),
  });
}

export async function toggleScriptFavorite(setId: string, scriptId: string, isFavorite: boolean): Promise<void> {
  await authFetch(`/opportunity-sets/${encodeURIComponent(setId)}/scripts/${encodeURIComponent(scriptId)}/favorite`, {
    method: "PATCH",
    body: JSON.stringify({ isFavorite }),
  });
}

export interface ScriptsFilters {
  topic?: string;
  concept?: string;
  presenter?: string;
  background?: string;
  sequence?: string;
  favoritesOnly?: boolean;
}

export interface FetchScriptsParams {
  setId: string;
  /** Opaque keyset token from the previous page's `nextCursor`; omit for the first page. */
  cursor?: string;
  /** Backend clamps to 1..100; default 20 when omitted. */
  limit?: number;
  search?: string;
  filters?: ScriptsFilters;
}

export interface FetchScriptsResponse {
  items: ScriptDto[];
  nextCursor: string | null;
  totalCount: number;
}

export async function fetchScripts(params: FetchScriptsParams): Promise<FetchScriptsResponse> {
  const qs = new URLSearchParams();
  if (params.cursor) qs.set("cursor", params.cursor);
  if (params.limit !== undefined) qs.set("limit", String(params.limit));
  if (params.search) qs.set("search", params.search);

  if (params.filters) {
    const { topic, concept, presenter, background, sequence, favoritesOnly } = params.filters;
    if (topic) qs.set("topic", topic);
    if (concept) qs.set("concept", concept);
    if (presenter) qs.set("presenter", presenter);
    if (background) qs.set("background", background);
    if (sequence) qs.set("sequence", sequence);
    // Boolean is sent as the literal "true"/"false" the backend Zod schema expects.
    if (favoritesOnly !== undefined) qs.set("favoritesOnly", String(favoritesOnly));
  }

  const url = `/opportunity-sets/${encodeURIComponent(params.setId)}/scripts${
    qs.size > 0 ? `?${qs.toString()}` : ""
  }`;
  const { data } = await authFetch<FetchScriptsResponse>(url);
  return data;
}

export interface ScriptsFacets {
  topics: string[];
  concepts: string[];
  presenters: string[];
  backgrounds: string[];
  sequences: string[];
}

export async function fetchScriptsFacets(setId: string): Promise<ScriptsFacets> {
  const { data } = await authFetch<ScriptsFacets>(
    `/opportunity-sets/${encodeURIComponent(setId)}/scripts/facets`,
  );
  return data;
}

export interface ExportScriptsParams {
  setId: string;
  search?: string;
  filters?: ScriptsFilters;
}

/**
 * Download the full filtered script set as an xlsx Blob.
 * Mirrors current search + filter state so the export matches what's on screen.
 * The `X-Row-Count` response header carries the actual row count so callers
 * can surface it in a success toast without parsing the workbook.
 */
export async function exportScriptsXlsx(params: ExportScriptsParams): Promise<{ blob: Blob; rowCount: number }> {
  const qs = new URLSearchParams()
  if (params.search) qs.set("search", params.search)
  if (params.filters) {
    const { topic, concept, presenter, background, sequence, favoritesOnly } = params.filters
    if (topic) qs.set("topic", topic)
    if (concept) qs.set("concept", concept)
    if (presenter) qs.set("presenter", presenter)
    if (background) qs.set("background", background)
    if (sequence) qs.set("sequence", sequence)
    if (favoritesOnly !== undefined) qs.set("favoritesOnly", String(favoritesOnly))
  }
  const url = `/opportunity-sets/${encodeURIComponent(params.setId)}/scripts/export.xlsx${qs.size > 0 ? `?${qs.toString()}` : ""}`
  const response = await authFetch(url, { rawResponse: true })
  const rowCount = Number(response.headers.get("X-Row-Count") ?? "0")
  const blob = await response.blob()
  return { blob, rowCount }
}

export interface ExportSelectedScriptsParams {
  setId: string;
  scriptIds: string[];
}

/**
 * Download a selected subset of scripts as an xlsx Blob.
 * Server re-fetches canonical rows by ID so the export reflects the DB state,
 * not client-side in-memory data.
 */
export async function exportSelectedScriptsXlsx(params: ExportSelectedScriptsParams): Promise<{ blob: Blob; rowCount: number }> {
  const url = `/opportunity-sets/${encodeURIComponent(params.setId)}/scripts/export.xlsx`
  const response = await authFetch(url, {
    rawResponse: true,
    method: "POST",
    body: JSON.stringify({ scriptIds: params.scriptIds }),
    headers: { "Content-Type": "application/json" },
  })
  const rowCount = Number(response.headers.get("X-Row-Count") ?? "0")
  const blob = await response.blob()
  return { blob, rowCount }
}

// ─── Amber Path ─────────────────────────────────────────────────────

interface AmberResult {
  correlationId: string;
}

async function amberPost(url: string, body: Record<string, unknown>): Promise<AmberResult> {
  const idempotencyKey = crypto.randomUUID();
  const { correlationId } = await authFetch<void>(url, {
    method: "POST",
    body: JSON.stringify({ ...body, idempotencyKey }),
  });
  return { correlationId: correlationId! };
}

// ─── Mutations ──────────────────────────────────────────────────────

export function generateScripts(payload: {
  tenantId: string;
  setId: string;
  opportunityIds: string[];
  filters?: Record<string, unknown>;
}): Promise<AmberResult> {
  return amberPost("/opportunities/generate", {
    ...payload,
    action: "scripts",
  });
}

export interface BulkRejectResult {
  updated: number;
  ids: (string | number)[];
}

export async function rejectOpportunities(payload: {
  tenantId: string;
  opportunityIds: string[];
  reason: string;
}): Promise<BulkRejectResult> {
  const { data } = await authFetch<BulkRejectResult>("/opportunities/reject", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return data;
}

// ─── List ────────────────────────────────────────────────────────────

export interface FetchOpportunitiesParams {
  setId: string;
  /** Opaque keyset token from the previous page's `nextCursor`. */
  cursor?: string;
  pageSize?: number;
  tab?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  action?: string;
  flowName?: string;
  feature?: string;
  subFeature?: string;
  engine?: string;
}

export interface FetchOpportunitiesResponse {
  items: Opportunity[];
  nextCursor: string | null;
  totalCount: number;
}

interface RawOpportunityRow {
  id: number;
  setId: string;
  state: string;
  flowName: string | null;
  topicTitle: string | null;
  topicDescription: string | null;
  findings: string | null;
  action: string | null;
  suggestion: string | null;
  spend: number | null;
  spendPerAngle: number | null;
  benchmarkSpend: number | null;
  benchmarkSpendPerAngle: number | null;
  competitorSpend: number | null;
  competitorSpendPerAngle: number | null;
  feature: string | null;
  subFeature: string | null;
  kpi: number | null;
  competitorKpi: number | null;
  benchmarkKpi: number | null;
  engine: string | null;
  industry: string | null;
  rejectionReason: string | null;
  runDate: string | null;
  segment1: string | null;
  segment2: string | null;
  segment3: string | null;
}

interface RawApiResponse {
  items: RawOpportunityRow[];
  nextCursor: string | null;
  totalCount: number;
}

function toOpportunity(r: RawOpportunityRow): Opportunity {
  return {
    id: String(r.id),
    opportunitySetId: r.setId,
    clientId: "",
    status: (r.state ?? "active") as Opportunity["status"],
    flowName: r.flowName ?? "",
    topicTitle: r.topicTitle ?? "",
    topicDescription: r.topicDescription ?? "",
    findings: r.findings ?? "",
    // Preserve a missing action as null. Defaulting null → "launch" previously
    // made action-less rows render a "Launch" badge that the Action facet/filter
    // (which match the real, null-aware value) could never return.
    action: (r.action ?? null) as Opportunity["action"],
    suggestion: r.suggestion ?? "",
    spend: r.engine?.includes("7d") && !r.spend ? null : (r.spend ?? 0),
    // Per-angle (share within subcategory) preserves null instead of coercing to
    // 0. A missing per-angle then degrades cleanly in getDualSpendDisplay (shows
    // the total as the single value) rather than rendering a misleading 0%.
    spendPerDataset: r.spendPerAngle ?? null,
    benchmarkSpend: r.engine?.includes("7d") && !r.benchmarkSpend ? null : (r.benchmarkSpend ?? 0),
    benchmarkSpendPerDataset: r.benchmarkSpendPerAngle ?? null,
    competitorSpend: r.engine?.includes("7d") && !r.competitorSpend ? null : (r.competitorSpend ?? 0),
    competitorSpendPerDataset: r.competitorSpendPerAngle ?? null,
    feature: r.feature ?? "",
    subFeature: r.subFeature ?? "",
    kpi: r.kpi ?? 0,
    competitorKpi: r.competitorKpi ?? 0,
    benchmarkKpi: r.benchmarkKpi ?? 0,
    engine: r.engine ?? "",
    industry: r.industry ?? "",
    rejectionReason: r.rejectionReason ?? null,
    runDate: r.runDate ?? null,
    creativeExamples: [],
    segment1: r.segment1 ?? null,
    segment2: r.segment2 ?? null,
    segment3: r.segment3 ?? null,
  };
}

export async function fetchOpportunities(
  params: FetchOpportunitiesParams,
): Promise<FetchOpportunitiesResponse> {
  const qs = new URLSearchParams();
  for (const [key, val] of Object.entries(params)) {
    if (val !== undefined && val !== null && val !== "") {
      qs.set(key, String(val));
    }
  }
  const { data } = await authFetch<RawApiResponse>(
    `/opportunities/list?${qs.toString()}`,
  );
  return {
    items: data.items.map(toOpportunity),
    nextCursor: data.nextCursor,
    totalCount: data.totalCount,
  };
}

// ─── Creatives ──────────────────────────────────────────────────────

export type CreativesGenerationStatus = "LOADING" | "READY" | "FAILED"

export interface OpportunityCreativesTotals {
  rowCount: number
  clientCount: number
  competitorCount: number
  benchmarkCount: number
}

export interface OpportunityCreativesResponse {
  items: CreativeExample[]
  creativesStatus: CreativesGenerationStatus | null
  nextCursor: string | null
  hasMore: boolean
  totals: OpportunityCreativesTotals
}

export interface FetchOpportunityCreativesParams {
  opportunityId: string
  cursor?: string
  pageSize?: number
}

export async function fetchOpportunityCreatives(
  params: FetchOpportunityCreativesParams,
): Promise<OpportunityCreativesResponse> {
  const qs = new URLSearchParams({ opportunityId: params.opportunityId });
  if (params.cursor) qs.set("cursor", params.cursor);
  if (params.pageSize != null) qs.set("pageSize", String(params.pageSize));

  const { data } = await authFetch<OpportunityCreativesResponse>(
    `/opportunities/creatives?${qs.toString()}`,
  );
  return data;
}

// ─── Facets ─────────────────────────────────────────────────────────

export interface OpportunityFacets {
  actions: string[];
  flowNames: string[];
  features: string[];
  subFeatures: string[];
  engines: string[];
}

export interface FetchOpportunityFacetsParams {
  setId: string;
  tab?: string;
  flowName?: string;
}

export async function fetchOpportunityFacets(
  params: FetchOpportunityFacetsParams,
): Promise<OpportunityFacets> {
  const qs = new URLSearchParams({ setId: params.setId });
  if (params.tab && params.tab !== "all") qs.set("tab", params.tab);
  if (params.flowName && params.flowName !== "all") qs.set("flowName", params.flowName);
  const { data } = await authFetch<OpportunityFacets>(`/opportunities/facets?${qs.toString()}`);
  return data;
}

// ─── Read Endpoints ─────────────────────────────────────────────────

export async function fetchBulkJobStatus(
  correlationId: string,
): Promise<BulkJobStatusResponse | null> {
  const { data } = await authFetch<BulkJobStatusResponse | null>(
    `/opportunities/status/${correlationId}`,
    { nullOn404: true },
  );
  return data;
}

// ─── Export (Amber Path) ─────────────────────────────────────────────────────

export type ExportRequestParams =
  | { mode: "selected"; tenantId: string; setId: string; opportunityIds: string[] }
  | { mode: "filtered"; tenantId: string; setId: string; tab?: string; search?: string; sortBy?: string; sortOrder?: "asc" | "desc"; action?: string; flowName?: string; feature?: string; subFeature?: string; engine?: string }
  | { mode: "all"; tenantId: string; setId: string };

/** Amber Path — enqueue CSV generation for an opportunity set. Returns a correlationId for polling. */
export function requestExport(payload: ExportRequestParams): Promise<AmberResult> {
  return amberPost("/opportunities/export", payload as Record<string, unknown>);
}

export async function cancelScripts({
  correlationId,
  tenantId,
}: {
  correlationId: string
  tenantId: string
}): Promise<void> {
  await authFetch("/opportunities/cancel", {
    method: "POST",
    body: JSON.stringify({ correlationId, tenantId }),
  })
}
